import type { SiteUpload, UploadPreview } from '@tabel/shared/schemas/sites';
import { STEP_BODY } from '@tabel/shared/sites/step-texts';
import { useQueryClient } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, FileCheck, Upload } from 'lucide-react';
import { useId, useState } from 'react';
import { useToast } from '../../../components/ErrorToast';
import { Button, Callout } from '../../../components/ui';
import { ruDate, ruDateTime } from '../../../lib/format';
import { useRole } from '../../../lib/role';
import * as api from '../api';
import { sitesKeys } from '../api';
import { SCREEN } from '../parts';
import { REPAIR } from '../texts';
import { useSite } from './actions';

/*
 * Загрузка выгрузки записей SmartPSS (007 T047; spec.md FR-027, FR-041, US4 сценарий 4; contracts/http-api.md,
 * «Загрузки выгрузок SmartPSS»): шаг 10 («Отметки за прошлые дни — по желанию») и раздел «Ремонт». Сначала
 * предпросмотр (`dryRun=1`): сколько строк распознано, какие колонки найдены, какие строки отклонены и почему, за какой
 * период. «Добавить» записывает файл; итог — «Добавлено N, повторов M, отклонено K» с номерами строк и причинами, без
 * ФИО. Числа и причины — только из ответа сервера; повторы считает сервер.
 */

/** Предел файла — contracts/http-api.md («файл до 20 МБ»): больше — сервер ответит `413 UPLOAD_TOO_LARGE`. */
const UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
/** Номеров отклонённых строк в одной строке списка — не больше: дальше «и ещё N». */
const ROWS_SHOWN = 20;

type Stage =
  | { kind: 'idle' }
  | { kind: 'checking' }
  | { kind: 'preview'; file: File; preview: UploadPreview }
  | { kind: 'adding'; file: File; preview: UploadPreview }
  | { kind: 'done'; result: SiteUpload };

export function RecordsUpload({
  inputId,
  disabled = false,
  primary = false,
}: {
  /** `id` поля файла: на него ведёт действие тревоги «Загрузить выгрузку». */
  inputId?: string;
  disabled?: boolean;
  /** «Добавить» — главное действие: только где на странице нет другого главного действия. */
  primary?: boolean;
}) {
  const id = useId();
  const fileId = inputId ?? `${id}-file`;
  const { detail } = useSite();
  const code = detail.site.code;
  const { role } = useRole();
  const qc = useQueryClient();
  const { showError } = useToast();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [tooLarge, setTooLarge] = useState(false);

  const check = async (file: File) => {
    setTooLarge(false);
    if (file.size > UPLOAD_MAX_BYTES) {
      setTooLarge(true);
      return;
    }
    setStage({ kind: 'checking' });
    try {
      setStage({ kind: 'preview', file, preview: await api.uploadPreview(code, 'records', file) });
    } catch (err) {
      setStage({ kind: 'idle' });
      showError(err, { screen: SCREEN, action: 'upload-records-preview' });
    }
  };

  const add = async () => {
    if (stage.kind !== 'preview') return;
    const { file, preview } = stage;
    setStage({ kind: 'adding', file, preview });
    try {
      const result = await api.upload(code, 'records', file);
      setStage({ kind: 'done', result });
      // Загрузка — в истории площадки; подсказка периода ремонта могла измениться.
      void qc.invalidateQueries({ queryKey: sitesKeys.site(role, code) });
      void qc.invalidateQueries({ queryKey: sitesKeys.repairHint(role, code) });
    } catch (err) {
      setStage({ kind: 'preview', file, preview });
      showError(err, { screen: SCREEN, action: 'upload-records' });
    }
  };

  const busy = stage.kind === 'checking' || stage.kind === 'adding';
  const reviewing = stage.kind === 'preview' || stage.kind === 'adding';

  return (
    <div className="flex flex-col gap-3">
      {!reviewing && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <FileButton
            id={fileId}
            label={STEP_BODY[10].historyUpload}
            disabled={disabled || busy}
            onFile={(f) => void check(f)}
          />
          {/* Ошибка встаёт на место подсказки (ux.md §13). */}
          <span id={`${fileId}-note`} className={`text-[12px] ${tooLarge ? 'text-bad' : 'text-ink-2'}`}>
            {tooLarge ? REPAIR.tooLarge : STEP_BODY[10].historyHint}
          </span>
        </div>
      )}
      <p aria-live="polite" className="text-[13px] text-ink-2 empty:hidden">
        {stage.kind === 'checking' ? REPAIR.checking : stage.kind === 'adding' ? REPAIR.adding : ''}
      </p>
      {reviewing && (
        <Preview
          preview={stage.preview}
          busy={stage.kind === 'adding'}
          primary={primary}
          onAdd={() => void add()}
          onCancel={() => setStage({ kind: 'idle' })}
        />
      )}
      {stage.kind === 'done' && <Result result={stage.result} />}
    </div>
  );
}

/** Предпросмотр: строки, колонки, период, отклонённые — и «Добавить» · «Отмена». */
function Preview({
  preview: p,
  busy,
  primary,
  onAdd,
  onCancel,
}: { preview: UploadPreview; busy: boolean; primary: boolean; onAdd: () => void; onCancel: () => void }) {
  const P = REPAIR.preview;
  const { detail } = useSite();
  const columns = (Object.keys(REPAIR.columnNames) as (keyof typeof REPAIR.columnNames)[])
    .filter((k) => p.columns[k])
    .map((k) => P.column(REPAIR.columnNames[k], p.columns[k] ?? ''));
  const nothing = p.recognized === 0;
  return (
    <div data-testid="upload-preview">
      <Callout tone="accent" icon={FileCheck}>
        <p>
          <b className="font-semibold">{P.title(p.fileName)}</b>
        </p>
        <p className="tabular-nums">
          {P.rows(p.recognized, p.rowsTotal)}
          {p.rejected > 0 && ` ${P.rejected(p.rejected)}`}
        </p>
        {p.timeRange ? (
          <p className="tabular-nums">
            {P.period(
              ruDateTime(p.timeRange.first, detail.site.tzOffset),
              ruDateTime(p.timeRange.last, detail.site.tzOffset),
            )}
          </p>
        ) : p.period ? (
          <p>{P.period(ruDate(p.period.from), ruDate(p.period.to))}</p>
        ) : null}
        {columns.length > 0 && <p>{P.columns(columns.join(', '))}</p>}
        <RejectedRows rows={p.rejectedRows} />
        <p className="mt-1 text-ink-2">{nothing ? P.nothing : P.note}</p>
      </Callout>
      {p.warnings.map((warning) => (
        <Callout key={warning} tone="warn" icon={CircleAlert}>
          {warning}
        </Callout>
      ))}
      <div className="mt-3 flex flex-wrap gap-2 max-sm:[&>button]:flex-auto">
        <Button variant={primary ? 'primary' : 'secondary'} disabled={busy || nothing} onClick={onAdd}>
          {P.add}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          {P.cancel}
        </Button>
      </div>
    </div>
  );
}

/** Итог записи: «Добавлено N отметок, повторов M, отклонено K» и отклонённые строки. */
function Result({ result: r }: { result: SiteUpload }) {
  return (
    <div data-testid="upload-result">
      <Callout tone="ok" icon={CircleCheck}>
        <p>
          <b className="font-semibold">{REPAIR.result.title(r.fileName)}</b>
        </p>
        <p className="tabular-nums">{REPAIR.result.text(r.added, r.duplicates, r.rejected)}</p>
        <RejectedRows rows={r.rejectedRows} counts={r.rejectedByReason} />
      </Callout>
    </div>
  );
}

/** Отклонённые строки по причинам: «Строки 4, 9 и 12: нет номера человека». Без ФИО: только номера строк. */
export function RejectedRows({
  rows,
  counts,
}: { rows: UploadPreview['rejectedRows']; counts?: SiteUpload['rejectedByReason'] }) {
  const byReason = new Map<keyof typeof REPAIR.rejectedReasons, number[]>();
  for (const r of rows) byReason.set(r.reason, [...(byReason.get(r.reason) ?? []), r.row]);
  for (const reason of Object.keys(REPAIR.rejectedReasons) as (keyof typeof REPAIR.rejectedReasons)[]) {
    if ((counts?.[reason] ?? 0) > 0 && !byReason.has(reason)) byReason.set(reason, []);
  }
  if (byReason.size === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-0.5 text-ink-2 tabular-nums">
      {[...byReason].map(([reason, nums]) => {
        const text = REPAIR.rejectedReasons[reason];
        return (
          <li key={reason}>
            {nums.length === 0
              ? REPAIR.rejectedCount(counts?.[reason] ?? 0, text)
              : (counts?.[reason] ?? nums.length) === 1
                ? REPAIR.rejectedRow(String(nums[0]), text)
                : REPAIR.rejectedRows(rowList(nums, counts?.[reason]), text)}
          </li>
        );
      })}
    </ul>
  );
}

/** «4, 9 и 12»; длинный список — первые номера и «ещё N»: «4, 9, … и ещё 30». */
function rowList(nums: readonly number[], total = nums.length): string {
  const shown = nums.slice(0, ROWS_SHOWN).map(String);
  const rest = total - shown.length;
  if (rest > 0) return `${shown.join(', ')} и ${REPAIR.more(rest)}`;
  const last = shown.pop();
  return shown.length ? `${shown.join(', ')} и ${last}` : (last ?? '');
}

/**
 * Кнопка выбора файла (как «Загрузить файл» шага 8): подпись поля — кнопка, поле скрыто, выбор файла открывает
 * браузер. Выбранный файл сбрасывается: тот же файл можно выбрать ещё раз.
 */
export function FileButton({
  id,
  label,
  disabled,
  onFile,
}: { id: string; label: string; disabled: boolean; onFile: (file: File) => void }) {
  return (
    <>
      <label
        htmlFor={id}
        aria-disabled={disabled || undefined}
        className={`inline-flex h-8 items-center gap-2 rounded-control border border-line bg-surface px-3 text-[13px] font-medium whitespace-nowrap text-ink transition-colors max-sm:h-10 ${
          disabled
            ? 'cursor-not-allowed text-ink-3'
            : 'cursor-pointer hover:border-line-strong hover:bg-subtle'
        } has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent`}
      >
        <Upload size={16} strokeWidth={1.75} aria-hidden />
        {label}
      </label>
      <input
        id={id}
        type="file"
        accept=".xlsx,.xls,.csv"
        className="sr-only"
        disabled={disabled}
        aria-describedby={`${id}-note`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </>
  );
}
